import { useMemo, useState } from "react";
import { Copy, Download, Info, Check } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { ScrollArea } from "@/components/ui/scroll-area";

/**
 * Renders the inline failure preview (3-line summary) + a "Show full
 * diagnostics" button that opens a centered Dialog with everything
 * we capture about a failed step. Has a one-click "Copy diagnostics"
 * button at the top of the dialog that puts a markdown-formatted blob
 * on the clipboard, ready to be pasted into Emergent's chat for triage.
 *
 * `step`: the run.step_results entry for a failed step. Has at
 *   minimum `{index, action, error, description}` and (for steps
 *   failed by the post-iteration-24 runner) a `diagnostics` block with
 *   structured per-strategy attempts, page context, browser info,
 *   console messages and network failures.
 *
 * `run`: the parent run document, used to surface run-level context
 *   (test name, ref, environment, framework, run id) inside the dialog.
 */
export function ErrorDetails({ step, run }) {
  const [open, setOpen] = useState(false);
  const diag = step?.diagnostics || null;

  const summaryLines = useMemo(() => {
    // Always render 3 lines of preview so the row's height is stable
    // across step results that have / don't have diagnostics yet.
    const out = [];
    out.push(step?.error || "Step failed with no error message.");
    if (diag?.resolution_attempts?.length) {
      out.push(
        `${diag.resolution_attempts.length} selector strategies attempted — none matched.`
      );
    } else if (diag) {
      out.push("No selector strategies were attempted for this step.");
    }
    if (diag?.page?.url) {
      out.push(`Page at failure: ${diag.page.url}`);
    } else if (diag?.browser?.browserbase_session_id) {
      out.push(`Browserbase session: ${diag.browser.browserbase_session_id}`);
    }
    return out;
  }, [step, diag]);

  const markdownBlob = useMemo(
    () => buildMarkdownDiagnostics(step, run, diag),
    [step, run, diag]
  );

  return (
    <div
      className="mt-2 rounded-md border border-[#FCA5A5] bg-[#FEF2F2] text-[#7F1D1D] text-[12px] p-3"
      data-testid={`step-error-${step.index}`}
    >
      <div className="space-y-1">
        {summaryLines.slice(0, 3).map((line, idx) => (
          <p
            key={idx}
            className="font-mono leading-relaxed break-words"
            data-testid={`step-error-line-${step.index}-${idx}`}
          >
            {idx === 0 ? line : <span className="text-[#9F1239]/80">{line}</span>}
          </p>
        ))}
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogTrigger asChild>
          <button
            type="button"
            className="mt-3 inline-flex items-center gap-1.5 rounded-md border border-[#B91C1C] bg-white px-2.5 py-1 text-[11px] font-semibold text-[#B91C1C] hover:bg-[#B91C1C] hover:text-white transition-colors"
            data-testid={`open-diagnostics-${step.index}`}
          >
            <Info size={12} strokeWidth={2.5} />
            Show full diagnostics
            {diag ? (
              <span className="ml-1 rounded-full bg-[#B91C1C]/10 px-1.5 py-px text-[9px] font-mono">
                {diag.resolution_attempts?.length || 0} attempts
              </span>
            ) : null}
          </button>
        </DialogTrigger>
        <DiagnosticsDialogContent
          step={step}
          run={run}
          diag={diag}
          markdownBlob={markdownBlob}
        />
      </Dialog>
    </div>
  );
}

function DiagnosticsDialogContent({ step, run, diag, markdownBlob }) {
  const [copied, setCopied] = useState(false);

  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(markdownBlob);
      setCopied(true);
      setTimeout(() => setCopied(false), 2200);
    } catch {
      // navigator.clipboard requires a secure context; fall back to a
      // hidden textarea so the button still works in older browsers.
      const ta = document.createElement("textarea");
      ta.value = markdownBlob;
      ta.style.position = "fixed";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.select();
      try {
        document.execCommand("copy");
        setCopied(true);
        setTimeout(() => setCopied(false), 2200);
      } finally {
        document.body.removeChild(ta);
      }
    }
  };

  const onDownload = () => {
    const blob = new Blob([JSON.stringify({ step, run_id: run?.id, diagnostics: diag }, null, 2)], {
      type: "application/json",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `probe-diagnostics-step${step.index + 1}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <DialogContent
      className="max-w-3xl rounded-xl border-[#E5E7EB] p-0 overflow-hidden"
      aria-describedby={undefined}
      data-testid={`diagnostics-dialog-${step.index}`}
    >
      <div className="flex items-center justify-between gap-3 pl-5 pr-12 py-4 border-b border-[#E5E7EB] bg-[#FEF2F2]">
        <div className="min-w-0">
          <DialogTitle className="font-['Geist'] font-semibold text-[16px] text-[#0F172A] truncate">
            Step {step.index + 1} diagnostics
          </DialogTitle>
          <p className="text-xs text-[#7F1D1D] mt-0.5 truncate">{step.description}</p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button
            type="button"
            onClick={onCopy}
            className="inline-flex items-center gap-1.5 rounded-md bg-[#0F172A] text-white px-3 py-1.5 text-[11px] font-semibold hover:bg-[#1E293B] transition-colors"
            data-testid={`copy-diagnostics-${step.index}`}
          >
            {copied ? <Check size={12} strokeWidth={3} /> : <Copy size={12} strokeWidth={2.5} />}
            {copied ? "Copied!" : "Copy diagnostics"}
          </button>
          <button
            type="button"
            onClick={onDownload}
            className="inline-flex items-center gap-1.5 rounded-md border border-[#CBD5E1] bg-white text-[#0F172A] px-3 py-1.5 text-[11px] font-semibold hover:border-[#3B82F6] transition-colors"
            data-testid={`download-diagnostics-${step.index}`}
          >
            <Download size={12} strokeWidth={2.5} />
            .json
          </button>
        </div>
      </div>
      <ScrollArea className="max-h-[70vh]">
        <div className="px-5 py-4">
          {!diag ? (
            <LegacyErrorOnly step={step} />
          ) : (
            <DiagnosticsSections step={step} run={run} diag={diag} />
          )}
        </div>
      </ScrollArea>
    </DialogContent>
  );
}

function LegacyErrorOnly({ step }) {
  return (
    <div className="space-y-2">
      <p className="text-xs text-[#64748B]">
        This run was executed before the diagnostics pipeline shipped
        (iteration 24). Only the raw error message is available below.
        Re-run the test to capture full diagnostics on the next failure.
      </p>
      <pre
        className="rounded-md border border-[#FCA5A5] bg-[#FEF2F2] text-[#7F1D1D] font-mono text-[11px] p-3 whitespace-pre-wrap break-words"
        data-testid="legacy-error-pre"
      >
        {step.error}
      </pre>
    </div>
  );
}

function DiagnosticsSections({ step, run, diag }) {
  const defaultOpen = ["error", "attempts"];
  // Auto-open the snapshot diff section if we have BOTH snapshots —
  // it's typically the highest-signal data for triage so the user
  // shouldn't have to dig for it.
  if (diag.replay_snapshot && diag.reference_snapshot?.snapshot) {
    defaultOpen.unshift("snapshot-diff");
  }
  // AI Recovery is the most human-readable triage artefact when the
  // engine engaged — surface it first.
  if (diag.ai_recovery) {
    defaultOpen.unshift("ai-recovery");
  }
  return (
    <Accordion type="multiple" defaultValue={defaultOpen} className="space-y-2">
      <Section value="error" title="Error" testId="diag-section-error">
        <pre className="font-mono text-[11px] whitespace-pre-wrap break-words bg-[#FEF2F2] text-[#7F1D1D] border border-[#FCA5A5] rounded-md p-3">
          {diag.error_message || step.error}
        </pre>
        {diag.python_traceback ? (
          <details className="mt-2">
            <summary className="text-[11px] text-[#64748B] cursor-pointer hover:text-[#0F172A]">
              Python traceback (last 2500 chars)
            </summary>
            <pre className="mt-2 font-mono text-[10px] whitespace-pre-wrap break-words bg-[#0F172A] text-[#E2E8F0] rounded-md p-3 max-h-72 overflow-y-auto">
              {diag.python_traceback}
            </pre>
          </details>
        ) : null}
      </Section>

      {diag.modal_rescue ? (
        <Section
          value="modal-rescue"
          title={
            diag.modal_rescue.success
              ? "🪄 Modal auto-dismissed by Probe"
              : "⚠️ Modal/overlay blocked this step"
          }
          testId="diag-section-modal-rescue"
        >
          <ModalRescueCard rescue={diag.modal_rescue} />
        </Section>
      ) : null}

      {diag.overlay_recovery ? (
        <Section
          value="overlay-recovery"
          title={
            diag.overlay_recovery.passed
              ? "🎯 Auto-healed via Overlay Recovery Engine"
              : "⚠️ Overlay Recovery Engine could not heal this step"
          }
          testId="diag-section-overlay-recovery"
        >
          <OverlayRecoveryCard recovery={diag.overlay_recovery} />
        </Section>
      ) : null}

      {diag.ai_recovery ? (
        <Section
          value="ai-recovery"
          title={
            diag.ai_recovery.outcome === "recovered"
              ? "🤖 AI Recovery Engine — state recovered"
              : diag.ai_recovery.outcome === "gave_up"
                ? "🤖 AI Recovery Engine — declared unrecoverable"
                : "🤖 AI Recovery Engine — could not recover"
          }
          testId="diag-section-ai-recovery"
        >
          <AIRecoveryCard recovery={diag.ai_recovery} />
        </Section>
      ) : null}

      <Section
        value="snapshot-diff"
        title={
          diag.reference_snapshot
            ? `📼 Recording vs failure (${diag.reference_snapshot.source === "record" ? "vs recording" : "vs last passing run"})`
            : "📼 Recording vs failure (no reference yet)"
        }
        testId="diag-section-snapshot-diff"
      >
        {!diag.replay_snapshot && !diag.reference_snapshot ? (
          <p className="text-[11px] text-[#64748B]">
            Snapshot capture wasn&apos;t available for this run (test was
            executed before this feature shipped). Next run will capture
            data automatically.
          </p>
        ) : !diag.reference_snapshot ? (
          <div className="space-y-2">
            <p className="text-[11px] text-[#64748B]">
              No reference snapshot yet — this test has never had a
              successful run, so there&apos;s nothing to compare against.
              The failure snapshot is shown below; once the test passes
              once, the next failure will get a full side-by-side diff.
            </p>
            <SnapshotDump label="Failure" snap={diag.replay_snapshot} />
          </div>
        ) : !diag.replay_snapshot ? (
          <div className="space-y-2">
            <p className="text-[11px] text-[#64748B]">
              Reference snapshot exists but failure snapshot wasn&apos;t
              captured (page died before snapshot ran).
            </p>
            <SnapshotDump label="Reference" snap={diag.reference_snapshot.snapshot} />
          </div>
        ) : (
          <SnapshotDiff
            reference={diag.reference_snapshot.snapshot}
            replay={diag.replay_snapshot}
            referenceSource={diag.reference_snapshot.source}
            referenceCapturedAt={diag.reference_snapshot.captured_at}
          />
        )}
      </Section>

      <Section
        value="attempts"
        title={`Selector strategies attempted (${diag.resolution_attempts?.length || 0})`}
        testId="diag-section-attempts"
      >
        {diag.resolution_attempts?.length ? (
          <table className="w-full text-[11px] font-mono" data-testid="diag-attempts-table">
            <thead className="text-left text-[#64748B]">
              <tr className="border-b border-[#E5E7EB]">
                <th className="py-1.5 pr-2">#</th>
                <th className="py-1.5 pr-2">Strategy</th>
                <th className="py-1.5 pr-2">Selector / Label</th>
                <th className="py-1.5 pr-2 text-right">Duration</th>
                <th className="py-1.5">Outcome</th>
              </tr>
            </thead>
            <tbody>
              {diag.resolution_attempts.map((a, idx) => (
                <tr key={idx} className="border-b border-[#F1F5F9]" data-testid={`diag-attempt-${idx}`}>
                  <td className="py-1.5 pr-2 text-[#94A3B8]">{idx + 1}</td>
                  <td className="py-1.5 pr-2 font-semibold text-[#0F172A]">{a.strategy}</td>
                  <td className="py-1.5 pr-2 text-[#475569] truncate max-w-[260px]" title={a.selector || ""}>
                    {a.selector || <em className="text-[#94A3B8]">—</em>}
                  </td>
                  <td className="py-1.5 pr-2 text-right text-[#475569]">{a.duration_ms ?? 0}ms</td>
                  <td className="py-1.5">
                    {a.ok ? (
                      <span className="inline-flex items-center gap-1 rounded bg-[#10B981]/10 text-[#047857] px-1.5 py-0.5 text-[10px] font-semibold">
                        passed
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 rounded bg-[#EF4444]/10 text-[#B91C1C] px-1.5 py-0.5 text-[10px] font-semibold" title={a.error || ""}>
                        miss
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p className="text-[11px] text-[#64748B]">
            No selector strategies were attempted (the failure was not
            a locator timeout). See the Error section above.
          </p>
        )}
      </Section>

      <Section value="page" title="Page context at failure" testId="diag-section-page">
        <Kv data={[
          ["URL", diag.page?.url],
          ["Title", diag.page?.title],
          ["Document.readyState", diag.page?.ready_state],
          ["Frame count", diag.page?.frame_count],
          ["DOM nodes", diag.page?.dom_node_count],
          ["DOM size (chars)", diag.page?.dom_size_chars],
          [
            "Viewport",
            diag.page?.viewport ? `${diag.page.viewport.width}×${diag.page.viewport.height}` : null,
          ],
        ]} />
        {diag.page?.iframe_urls?.length ? (
          <div className="mt-2">
            <p className="text-[11px] font-semibold text-[#0F172A]">Iframe URLs</p>
            <ul className="mt-1 list-disc list-inside text-[11px] font-mono text-[#475569]">
              {diag.page.iframe_urls.map((u, idx) => (
                <li key={idx} className="truncate">{u}</li>
              ))}
            </ul>
          </div>
        ) : null}
      </Section>

      <Section value="browser" title="Browser & framework" testId="diag-section-browser">
        <Kv data={[
          ["Engine", diag.browser?.engine],
          ["Browserbase session id", diag.browser?.browserbase_session_id],
          ["Framework", diag.framework?.name],
          ["Framework version", diag.framework?.version],
          ["Probe git commit", diag.probe?.git_commit],
          ["Playwright", diag.probe?.playwright],
          ["Python", diag.probe?.python],
          ["Platform", diag.probe?.platform],
        ]} />
      </Section>

      <Section value="step" title="Step config" testId="diag-section-step">
        <Kv data={[
          ["ID", diag.step_config?.id],
          ["Action", diag.step_config?.action],
          ["Selector", diag.step_config?.selector],
          ["Description", diag.step_config?.description],
          ["recordedSchemaVersion", diag.step_config?.recordedSchemaVersion],
          ["Recorded duration", formatDurationFromMs(diag.step_config?.recorded_duration_ms)],
          ["Timeout override", formatDurationFromMs(diag.step_config?.timeout_ms)],
          ["Step duration", formatDurationFromMs(diag.step_duration_ms)],
        ]} />
        {diag.step_config?.elementMetadata ? (
          <details className="mt-2">
            <summary className="text-[11px] text-[#64748B] cursor-pointer hover:text-[#0F172A]">
              elementMetadata
            </summary>
            <pre className="mt-2 font-mono text-[10px] whitespace-pre-wrap break-words bg-[#F8FAFC] border border-[#E5E7EB] rounded-md p-3 max-h-60 overflow-y-auto">
              {JSON.stringify(diag.step_config.elementMetadata, null, 2)}
            </pre>
          </details>
        ) : null}
        {diag.step_config?.selectorCandidates?.length ? (
          <details className="mt-2">
            <summary className="text-[11px] text-[#64748B] cursor-pointer hover:text-[#0F172A]">
              selectorCandidates ({diag.step_config.selectorCandidates.length})
            </summary>
            <pre className="mt-2 font-mono text-[10px] whitespace-pre-wrap break-words bg-[#F8FAFC] border border-[#E5E7EB] rounded-md p-3 max-h-60 overflow-y-auto">
              {JSON.stringify(diag.step_config.selectorCandidates, null, 2)}
            </pre>
          </details>
        ) : null}
      </Section>

      <Section value="run" title="Run context" testId="diag-section-run">
        <Kv data={[
          ["Run id", diag.run_context?.run_id],
          ["Test", diag.run_context?.test_name],
          ["Test ref", diag.run_context?.test_ref || run?.test_ref],
          ["Environment", diag.run_context?.environment_name],
          ["Environment type", diag.run_context?.environment_type],
          ["Environment id", diag.run_context?.environment_id],
          ["Workspace id", diag.run_context?.workspace_id],
          ["Project id", diag.run_context?.project_id],
          ["Suite id", diag.run_context?.suite_id],
        ]} />
      </Section>

      <Section
        value="console"
        title={`Browser console + page errors (${(diag.console_messages?.length || 0) + (diag.page_errors?.length || 0)})`}
        testId="diag-section-console"
      >
        {!diag.console_messages?.length && !diag.page_errors?.length ? (
          <p className="text-[11px] text-[#64748B]">No console output or page errors captured during this step.</p>
        ) : (
          <div className="space-y-2">
            {diag.console_messages?.map((m, idx) => (
              <div
                key={`c-${idx}`}
                className="font-mono text-[11px] rounded-md border border-[#E5E7EB] bg-[#F8FAFC] px-2.5 py-1.5"
                data-testid={`diag-console-${idx}`}
              >
                <span className={`uppercase text-[9px] font-semibold mr-2 ${consoleTone(m.type)}`}>{m.type}</span>
                <span className="text-[#0F172A] break-all">{m.text}</span>
                {m.location?.url ? (
                  <span className="block text-[10px] text-[#94A3B8] mt-0.5">
                    @ {m.location.url}:{m.location.lineNumber ?? "?"}:{m.location.columnNumber ?? "?"}
                  </span>
                ) : null}
              </div>
            ))}
            {diag.page_errors?.map((p, idx) => (
              <div
                key={`p-${idx}`}
                className="font-mono text-[11px] rounded-md border border-[#FCA5A5] bg-[#FEF2F2] px-2.5 py-1.5"
                data-testid={`diag-pageerror-${idx}`}
              >
                <span className="uppercase text-[9px] font-semibold mr-2 text-[#B91C1C]">pageerror</span>
                <span className="text-[#7F1D1D] break-all">{p.message}</span>
              </div>
            ))}
          </div>
        )}
      </Section>

      <Section
        value="network"
        title={`Failed network requests (${diag.network_failures?.length || 0})`}
        testId="diag-section-network"
      >
        {!diag.network_failures?.length ? (
          <p className="text-[11px] text-[#64748B]">No network failures during this step.</p>
        ) : (
          <ul className="space-y-1 font-mono text-[11px]">
            {diag.network_failures.map((n, idx) => (
              <li
                key={idx}
                className="rounded-md border border-[#FED7AA] bg-[#FFFBEB] px-2.5 py-1.5"
                data-testid={`diag-network-${idx}`}
              >
                <span className="font-semibold text-[#7C2D12] mr-2">{n.method}</span>
                <span className="text-[#0F172A] break-all">{n.url}</span>
                {n.failure ? (
                  <span className="block text-[10px] text-[#9A3412] mt-0.5">{n.failure}</span>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </Section>
    </Accordion>
  );
}

function Section({ value, title, testId, children }) {
  return (
    <AccordionItem
      value={value}
      className="border border-[#E5E7EB] rounded-md overflow-hidden"
      data-testid={testId}
    >
      <AccordionTrigger className="px-3 py-2 text-[12px] font-semibold text-[#0F172A] hover:no-underline">
        {title}
      </AccordionTrigger>
      <AccordionContent className="px-3 pb-3 pt-1">{children}</AccordionContent>
    </AccordionItem>
  );
}

function ModalRescueCard({ rescue }) {
  // Render the modal-rescue outcome with three pieces of info:
  // (1) the user-facing hint sentence, (2) which tier won (or that
  // none did), and (3) a compact summary of the overlay Probe
  // detected. Reads `diag.modal_rescue` written by runner.py when an
  // interactive step ran into a "subtree intercepts pointer events"
  // Playwright timeout.
  const success = !!rescue?.success;
  const tier = rescue?.tier;
  const tieredAttempts = Array.isArray(rescue?.tried_tiers) ? rescue.tried_tiers : [];
  const summary = rescue?.overlay_summary;
  return (
    <div className="space-y-3">
      <div
        className={`rounded-md px-3 py-2 text-[12px] ${
          success
            ? "bg-[#ECFDF5] border border-[#A7F3D0] text-[#065F46]"
            : "bg-[#FEF3C7] border border-[#FCD34D] text-[#92400E]"
        }`}
        data-testid="modal-rescue-hint"
      >
        {rescue?.hint || (
          success
            ? "Probe auto-dismissed an overlay that was blocking this step."
            : "An overlay was blocking this step and Probe couldn't dismiss it."
        )}
      </div>
      <Kv
        data={[
          ["Outcome", success ? `Dismissed via ${tier}` : "Could not dismiss"],
          ["Duration", rescue?.duration_ms != null ? `${rescue.duration_ms} ms` : null],
          ["Overlay role", summary?.role || summary?.ariaLabel || (summary ? "(unknown)" : null)],
          ["Overlay z-index", summary?.z != null ? String(summary.z) : null],
          [
            "Overlay text",
            summary?.text ? `${summary.text.slice(0, 120)}${summary.text.length > 120 ? "…" : ""}` : null,
          ],
        ]}
      />
      {tieredAttempts.length > 0 ? (
        <details>
          <summary className="text-[11px] text-[#64748B] cursor-pointer hover:text-[#0F172A]">
            Tier-by-tier attempts ({tieredAttempts.length})
          </summary>
          <ul className="mt-2 space-y-1 text-[11px]">
            {tieredAttempts.map((t, i) => (
              <li
                key={i}
                className="flex items-center gap-2 font-mono"
                data-testid={`modal-rescue-tier-${i}`}
              >
                <span
                  className={`inline-block w-2 h-2 rounded-full ${
                    t.dismissed ? "bg-[#10B981]" : "bg-[#94A3B8]"
                  }`}
                />
                <span className="text-[#0F172A]">{t.tier}</span>
                <span className="text-[#64748B]">
                  {t.dismissed ? "✓ dismissed" : "✗ no effect"}
                </span>
              </li>
            ))}
          </ul>
        </details>
      ) : null}
      {!success ? (
        <p className="text-[11px] text-[#64748B]">
          To fix this permanently: add an explicit step that dismisses
          this overlay before step {(rescue?.step_index ?? 0) + 1}, or
          re-record the test with the modal already in its dismissed
          state.
        </p>
      ) : null}
    </div>
  );
}


function OverlayRecoveryCard({ recovery }) {
  // Renders the Overlay Recovery Engine's per-stage trace + the
  // owner-control before/after values. Reads `diag.overlay_recovery`
  // written by runner.py when a step's shape matched an overlay
  // pattern (datepicker, dropdown, autocomplete, menu, cascading
  // control) and the runner routed the click through the generic
  // engine instead of the raw resolve→click path.
  const passed = !!recovery?.passed;
  const isNav = recovery?.step_kind === "navigation";
  const events = Array.isArray(recovery?.events) ? recovery.events : [];
  const signals = Array.isArray(recovery?.signals) ? recovery.signals : [];
  return (
    <div className="space-y-3" data-testid="overlay-recovery-card">
      <div
        className={`rounded-md px-3 py-2 text-[12px] ${
          passed
            ? "bg-[#ECFDF5] border border-[#A7F3D0] text-[#065F46]"
            : "bg-[#FEF3C7] border border-[#FCD34D] text-[#92400E]"
        }`}
        data-testid="overlay-recovery-hint"
      >
        {passed
          ? isNav
            ? `Probe replayed this step as overlay navigation inside overlay session ${recovery.session_id || ""} (${recovery.overlay_type || "unknown"}). The owner control stays sticky for the whole session; value verification is deferred to the final selection step.`
            : `Probe detected this step targets an overlay item (${recovery.overlay_type || "unknown"}) and healed the replay by ensuring the owner control opened the overlay and verifying the selection stuck.`
          : recovery?.fallback === "standard-replay"
            ? "The overlay engine couldn't complete this session step, so Probe fell back to the standard replay path (the click may be a regular control that only looked like overlay navigation)."
            : "Probe detected this step targets an overlay item but could not confidently map it to an owning control. Please re-record this step from a clean state so the owner mapping can be captured."}
      </div>
      <Kv
        data={[
          ["Overlay type", recovery?.overlay_type || "unknown"],
          ["Stage", recovery?.stage || null],
          ["Session", recovery?.session_id || null],
          ["Step kind", recovery?.step_kind || null],
          ["Owner", recovery?.owner || null],
          ["Owner source", recovery?.owner_source || null],
          ["Owner confidence", recovery?.owner_confidence != null ? String(recovery.owner_confidence) : null],
          ["Auto-open handoff", recovery?.auto_open_handoff ? "yes — new session started for next step" : null],
          ["Confidence", recovery?.confidence != null ? String(recovery.confidence) : null],
          ["Signals", signals.length ? signals.join(", ") : null],
          ["Owner value before", recovery?.before_value || null],
          ["Owner value after", recovery?.after_value || null],
        ]}
      />
      {events.length > 0 ? (
        <details open>
          <summary className="text-[11px] text-[#64748B] cursor-pointer hover:text-[#0F172A]">
            Recovery trace ({events.length} events)
          </summary>
          <ul className="mt-2 space-y-1 text-[11px]" data-testid="overlay-recovery-events">
            {events.map((e, i) => (
              <li key={i} className="flex items-start gap-2 font-mono">
                <span
                  className={`inline-block w-1.5 h-1.5 rounded-full mt-1.5 shrink-0 ${
                    e.ok ? "bg-[#10B981]" : "bg-[#F59E0B]"
                  }`}
                />
                <span className="text-[#94A3B8] shrink-0">
                  {String(e.ts_ms || 0).padStart(4, "0")}ms
                </span>
                <span className="text-[#0F172A] shrink-0">{e.stage}</span>
                <span className="text-[#334155]">{e.message}</span>
              </li>
            ))}
          </ul>
        </details>
      ) : null}
    </div>
  );
}



/** Human-readable one-liner for an AI recovery action descriptor. */
function summarizeAction(a) {
  if (!a) return "";
  const t = a.target || {};
  const name = t.name || t.label || t.text || t.placeholder || t.css || "";
  switch (a.type) {
    case "replay_recorded_step":
      return `replay recorded step ${Number(a.step_index) + 1}${a.description ? ` (${a.description})` : ""}`;
    case "click":
      return `click '${name}'`;
    case "fill":
      return `fill '${name}' with '${a.value}'`;
    case "select_option":
      return `select '${a.value}' in '${name}'`;
    case "press_key":
      return `press ${a.value}`;
    case "scroll_into_view":
      return `scroll to '${name}'`;
    case "wait":
      return `wait ${a.value}ms`;
    case "press_escape":
      return "press Escape";
    default:
      return JSON.stringify(a);
  }
}

/**
 * AI Recovery Engine (Iter 43) — renders the Understand → Diagnose →
 * Plan → Recover → Verify journey. Reads `diag.ai_recovery` (failed
 * steps, inside the diagnostics dialog) or `sr.ai_recovery` (recovered
 * steps, inline on the run page via AIRecoveryDetails).
 */
function AIRecoveryCard({ recovery }) {
  const recovered = recovery?.outcome === "recovered";
  const iterations = Array.isArray(recovery?.iterations) ? recovery.iterations : [];
  const outcomeText = {
    recovered: recovery?.used_hint
      ? "Probe replayed a previously learned recovery deterministically (no AI call needed), confirmed the original step's target was restored, then replayed the failed step."
      : "The AI Recovery Engine diagnosed why replay failed, executed a verified chain of safe recovery actions, confirmed the original step's target was restored (goal check), then replayed the failed step successfully.",
    gave_up: `The AI judged this state unrecoverable from the UI${recovery?.give_up_reason ? `: ${recovery.give_up_reason}` : "."}`,
    failed: "The AI Recovery Engine engaged but could not restore the original step's target within its iteration budget.",
    skipped: recovery?.skip_reason || "AI Recovery was skipped.",
    error: recovery?.error ? `AI Recovery errored: ${recovery.error}` : "AI Recovery errored.",
  }[recovery?.outcome] || "AI Recovery was triggered.";
  return (
    <div className="space-y-3" data-testid="ai-recovery-card">
      <div
        className={`rounded-md px-3 py-2 text-[12px] ${
          recovered
            ? "bg-[#ECFDF5] border border-[#A7F3D0] text-[#065F46]"
            : "bg-[#FEF3C7] border border-[#FCD34D] text-[#92400E]"
        }`}
        data-testid="ai-recovery-hint"
      >
        {outcomeText}
      </div>
      <Kv
        data={[
          ["Outcome", recovery?.outcome],
          ["Diagnosis", recovery?.final_diagnosis],
          ["Category", recovery?.final_category],
          ["Blocking control", recovery?.final_blocking_control],
          ["Missing prerequisite", recovery?.missing_prerequisite],
          ["Recovery goal", recovery?.recovery_goal],
          ["Recovery actions", recovery?.recovery_action_summary],
          ["Learned hint used", recovery?.used_hint ? "yes — deterministic replay, no LLM call" : null],
          ["Step retry", recovery?.step_retry],
          ["Model", recovery?.model],
          ["Total duration", recovery?.total_duration_ms != null ? `${(recovery.total_duration_ms / 1000).toFixed(1)} s` : null],
        ]}
      />
      {iterations.map((it, i) => (
        <div
          key={i}
          className="rounded-md border border-[#E5E7EB] bg-white px-3 py-2.5 space-y-2"
          data-testid={`ai-recovery-iteration-${i}`}
        >
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[11px] font-semibold text-[#0F172A]">
              {it.source === "learned-hint" ? "Learned hint" : `Iteration ${it.n}`}
            </span>
            {it.category ? (
              <span className="px-1.5 py-0.5 rounded bg-[#EDE9FE] text-[#5B21B6] text-[10px] font-medium font-mono">
                {it.category}
              </span>
            ) : null}
            {it.confidence != null ? (
              <span className="font-mono text-[10px] text-[#94A3B8]">
                conf {Math.round(it.confidence * 100)}%
              </span>
            ) : null}
            <span
              className={`ml-auto px-1.5 py-0.5 rounded text-[10px] font-semibold ${
                it.verified
                  ? "bg-[#10B981]/10 text-[#047857]"
                  : "bg-[#F59E0B]/10 text-[#B45309]"
              }`}
            >
              {it.result || (it.verified ? "verified" : "pending")}
            </span>
          </div>
          {it.diagnosis ? (
            <p className="text-[11px] text-[#334155] leading-relaxed">
              <span className="font-semibold text-[#0F172A]">Diagnosis:</span> {it.diagnosis}
              {it.blocking_control ? (
                <span className="text-[#64748B]"> · blocking: {it.blocking_control}</span>
              ) : null}
            </p>
          ) : null}
          {it.recovery_goal ? (
            <p className="text-[11px] text-[#334155] leading-relaxed" data-testid={`ai-recovery-goal-${i}`}>
              <span className="font-semibold text-[#0F172A]">Goal:</span> {it.recovery_goal}
              {it.missing_prerequisite ? (
                <span className="text-[#64748B]"> · missing: {it.missing_prerequisite}</span>
              ) : null}
            </p>
          ) : null}
          {Array.isArray(it.plan) && it.plan.length > 0 ? (
            <div className="space-y-1" data-testid={`ai-recovery-plan-${i}`}>
              <p className="text-[10px] font-semibold text-[#64748B] uppercase tracking-wide">
                Recovery plan
              </p>
              <ol className="space-y-0.5">
                {it.plan.map((p, pi) => (
                  <li key={pi} className="flex items-start gap-1.5 text-[11px] font-mono">
                    <span
                      className={
                        p.status === "passed"
                          ? "text-[#10B981]"
                          : p.status === "skipped"
                            ? "text-[#94A3B8]"
                            : "text-[#B91C1C]"
                      }
                    >
                      {p.status === "passed" ? "✓" : p.status === "skipped" ? "–" : "✗"}
                    </span>
                    <span className="text-[#475569] break-all">{summarizeAction(p.action)}</span>
                    {p.execution_error ? (
                      <span className="text-[#B91C1C] break-all">— {p.execution_error}</span>
                    ) : null}
                  </li>
                ))}
              </ol>
              {it.plan_abort_reason ? (
                <p className="text-[10px] text-[#B45309]">{it.plan_abort_reason}</p>
              ) : null}
            </div>
          ) : null}
          {it.action ? (
            <p className="font-mono text-[10px] text-[#475569] break-all">
              <span className="font-semibold text-[#0F172A] font-sans">Action:</span>{" "}
              {JSON.stringify(it.action)}
              {it.executed === false && it.execution_error ? (
                <span className="text-[#B91C1C]"> — {it.execution_error}</span>
              ) : null}
            </p>
          ) : null}
          {Array.isArray(it.verification) && it.verification.length > 0 ? (
            <ul className="space-y-0.5">
              {it.verification.map((v, vi) => (
                <li key={vi} className="flex items-start gap-1.5 text-[11px] font-mono">
                  <span className={v.passed ? "text-[#10B981]" : "text-[#B91C1C]"}>
                    {v.passed ? "✓" : "✗"}
                  </span>
                  <span className="text-[#0F172A]">{v.kind}</span>
                  {v.detail ? <span className="text-[#94A3B8] break-all">{v.detail}</span> : null}
                </li>
              ))}
            </ul>
          ) : null}
          {Array.isArray(it.goal_verification) && it.goal_verification.length > 0 ? (
            <div className="space-y-0.5">
              <p className="text-[10px] font-semibold text-[#64748B] uppercase tracking-wide">
                Goal verification
              </p>
              <ul className="space-y-0.5">
                {it.goal_verification.map((v, vi) => (
                  <li key={vi} className="flex items-start gap-1.5 text-[11px] font-mono">
                    <span className={v.passed ? "text-[#10B981]" : "text-[#B91C1C]"}>
                      {v.passed ? "✓" : "✗"}
                    </span>
                    <span className="text-[#0F172A]">{v.kind}</span>
                    {v.detail ? <span className="text-[#94A3B8] break-all">{v.detail}</span> : null}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
          {it.goal_check ? (
            <p
              className="flex items-start gap-1.5 text-[11px] font-mono"
              data-testid={`ai-recovery-goal-check-${i}`}
            >
              <span className={it.goal_check.passed ? "text-[#10B981]" : "text-[#D97706]"}>
                {it.goal_check.passed ? "✓" : "→"}
              </span>
              <span className="text-[#0F172A]">goal check</span>
              <span className="text-[#94A3B8]">
                {it.goal_check.passed
                  ? `original step target located again (${it.goal_check.matched || "resolved"})`
                  : "original step target still absent — recovery chain continues"}
              </span>
            </p>
          ) : null}
          {it.evidence_summary ? (
            <p className="text-[10px] text-[#94A3B8] font-mono">
              evidence: {it.evidence_summary.interactive_elements ?? 0} elements ·{" "}
              {it.evidence_summary.dialogs ?? 0} dialogs · {it.evidence_summary.alerts ?? 0} alerts ·{" "}
              {it.evidence_summary.validation_messages ?? 0} validations ·{" "}
              {it.evidence_summary.toasts ?? 0} toasts · {it.evidence_summary.spinners ?? 0} spinners
              {it.evidence_summary.aria_snapshot ? " · a11y tree" : ""}
              {it.evidence_summary.screenshot ? " · screenshot" : ""}
            </p>
          ) : null}
        </div>
      ))}
      {iterations.length === 0 ? (
        <p className="text-[11px] text-[#64748B]">No recovery iterations were executed.</p>
      ) : null}
    </div>
  );
}

/** Collapsible inline wrapper for AI recovery details on RECOVERED
 *  (passed) steps, where the red ErrorDetails box doesn't render. */
export function AIRecoveryDetails({ recovery, index }) {
  if (!recovery) return null;
  return (
    <details
      className="mt-2 rounded-md border border-[#C4B5FD] bg-[#F5F3FF] px-3 py-2"
      data-testid={`ai-recovery-inline-${index}`}
    >
      <summary className="text-[11px] font-semibold text-[#5B21B6] cursor-pointer hover:text-[#4C1D95]">
        🤖 AI Recovery journey — diagnosis, action &amp; verification
      </summary>
      <div className="mt-3">
        <AIRecoveryCard recovery={recovery} />
      </div>
    </details>
  );
}


function Kv({ data }) {
  const rows = data.filter(([, v]) => v !== null && v !== undefined && v !== "");
  if (rows.length === 0) {
    return <p className="text-[11px] text-[#64748B]">No data captured.</p>;
  }
  return (
    <dl className="grid grid-cols-[160px_1fr] gap-x-3 gap-y-1.5 text-[11px]">
      {rows.map(([label, value]) => (
        <div key={label} className="contents">
          <dt className="text-[#64748B] font-medium">{label}</dt>
          <dd className="font-mono text-[#0F172A] break-all">{String(value)}</dd>
        </div>
      ))}
    </dl>
  );
}

function consoleTone(type) {
  if (type === "error") return "text-[#B91C1C]";
  if (type === "warning" || type === "warn") return "text-[#B45309]";
  if (type === "info") return "text-[#1D4ED8]";
  return "text-[#475569]";
}

/** Format a millisecond value as a human-readable duration. Used in the
 *  diagnostics dialog for step-level numbers (step duration, timeout
 *  budget, recorded duration) where seconds are the natural unit.
 *  Returns null if the input is null/undefined so empty rows get
 *  filtered out by the `Kv` helper. */
function formatDurationFromMs(ms) {
  if (ms === null || ms === undefined || ms === "") return null;
  const n = Number(ms);
  if (!Number.isFinite(n)) return String(ms);
  if (n < 1000) return `${n} ms`;
  // ≥ 1 s → show seconds with one decimal, drop trailing .0
  const secs = n / 1000;
  return `${Number(secs.toFixed(1))} s`;
}

/** Render a side-by-side diff between two snapshots. Each row picks a
 *  field, formats reference + replay values, and computes a delta
 *  marker: ✓ same | ⚠ changed. */
function SnapshotDiff({ reference, replay, referenceSource, referenceCapturedAt }) {
  const rows = buildDiffRows(reference, replay);
  const changedCount = rows.filter((r) => r.changed && !r.neutral).length;
  return (
    <div className="space-y-3">
      <div className="text-[11px] text-[#64748B] leading-relaxed">
        Reference: <strong className="text-[#0F172A]">{referenceSource === "record" ? "recording-time snapshot" : "last passing run"}</strong>
        {referenceCapturedAt ? <> · captured {referenceCapturedAt}</> : null}
        {" · "}
        <strong className={changedCount > 0 ? "text-[#B91C1C]" : "text-[#047857]"}>
          {changedCount} field{changedCount === 1 ? "" : "s"} changed
        </strong>
      </div>
      <table className="w-full text-[11px] font-mono" data-testid="snapshot-diff-table">
        <thead className="text-left text-[#64748B]">
          <tr className="border-b border-[#E5E7EB]">
            <th className="py-1.5 pr-2 w-[180px]">Field</th>
            <th className="py-1.5 pr-2">At recording / last pass</th>
            <th className="py-1.5 pr-2">At failure</th>
            <th className="py-1.5 w-[80px]">Δ</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, idx) => (
            <tr
              key={idx}
              className={`border-b border-[#F1F5F9] align-top ${r.changed && !r.neutral ? "bg-[#FEF2F2]/50" : ""}`}
              data-testid={`snapshot-diff-row-${idx}`}
            >
              <td className="py-1.5 pr-2 text-[#0F172A] font-semibold">{r.label}</td>
              <td className="py-1.5 pr-2 text-[#475569] break-all max-w-[260px]">
                {r.referenceDisplay ?? <em className="text-[#94A3B8]">—</em>}
              </td>
              <td className={`py-1.5 pr-2 break-all max-w-[260px] ${r.changed && !r.neutral ? "text-[#B91C1C]" : "text-[#475569]"}`}>
                {r.replayDisplay ?? <em className="text-[#94A3B8]">—</em>}
              </td>
              <td className="py-1.5 text-[10px]">
                {r.changed
                  ? (
                    <span className={r.neutral ? "text-[#94A3B8]" : "text-[#B91C1C] font-semibold"}>
                      {r.deltaIcon || "⚠"} {r.deltaText || "changed"}
                    </span>
                  )
                  : <span className="text-[#10B981]">✓ same</span>
                }
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function buildDiffRows(ref, rep) {
  const eq = (a, b) => JSON.stringify(a) === JSON.stringify(b);
  const numDelta = (refN, repN) => {
    if (refN === null || repN === null || refN === undefined || repN === undefined) return null;
    const d = repN - refN;
    if (d === 0) return null;
    const sign = d > 0 ? "+" : "";
    return `${sign}${d.toLocaleString()}`;
  };
  const rows = [];
  rows.push({
    label: "URL",
    referenceDisplay: ref.url ?? null,
    replayDisplay: rep.url ?? null,
    changed: !eq(ref.url, rep.url),
  });
  rows.push({
    label: "Page title",
    referenceDisplay: ref.title ?? null,
    replayDisplay: rep.title ?? null,
    changed: !eq(ref.title, rep.title),
  });
  const refEl = ref.element || null;
  const repEl = rep.element || null;
  const refHasEl = !!(refEl && refEl.outerHTML);
  const repHasEl = !!(repEl && repEl.outerHTML);
  rows.push({
    label: "Target element",
    referenceDisplay: refHasEl ? `<${refEl.tagName || "el"}> present` : "not found",
    replayDisplay: repHasEl ? `<${repEl.tagName || "el"}> present` : "not found",
    changed: refHasEl !== repHasEl,
    deltaIcon: refHasEl && !repHasEl ? "🚫" : undefined,
    deltaText: refHasEl && !repHasEl ? "element gone" : undefined,
  });
  if (refHasEl && repHasEl && !eq(refEl.outerHTML, repEl.outerHTML)) {
    rows.push({
      label: "Element outerHTML",
      referenceDisplay: <code className="break-all whitespace-pre-wrap text-[10px]">{refEl.outerHTML}</code>,
      replayDisplay: <code className="break-all whitespace-pre-wrap text-[10px]">{repEl.outerHTML}</code>,
      changed: true,
    });
  }
  if (refEl || repEl) {
    rows.push({
      label: "Element visible",
      referenceDisplay: refEl ? String(refEl.visible) : null,
      replayDisplay: repEl ? String(repEl.visible) : null,
      changed: refEl?.visible !== repEl?.visible,
    });
  }
  if (refEl?.bounding_rect || repEl?.bounding_rect) {
    const refR = refEl?.bounding_rect;
    const repR = repEl?.bounding_rect;
    const f = (r) => r ? `${r.x},${r.y} ${r.w}×${r.h}` : null;
    rows.push({
      label: "Bounding rect",
      referenceDisplay: f(refR),
      replayDisplay: f(repR),
      changed: !eq(refR, repR),
    });
  }
  const delta = numDelta(ref.dom_node_count, rep.dom_node_count);
  rows.push({
    label: "DOM nodes",
    referenceDisplay: ref.dom_node_count?.toLocaleString() ?? null,
    replayDisplay: rep.dom_node_count?.toLocaleString() ?? null,
    changed: ref.dom_node_count !== rep.dom_node_count,
    deltaText: delta ? `Δ ${delta}` : undefined,
  });
  if (ref.ready_state || rep.ready_state) {
    rows.push({
      label: "Document.readyState",
      referenceDisplay: ref.ready_state ?? null,
      replayDisplay: rep.ready_state ?? null,
      changed: ref.ready_state !== rep.ready_state,
    });
  }
  if (ref.frame_count != null || rep.frame_count != null) {
    rows.push({
      label: "Frame count",
      referenceDisplay: ref.frame_count ?? null,
      replayDisplay: rep.frame_count ?? null,
      changed: ref.frame_count !== rep.frame_count,
      neutral: true,
    });
  }
  const refC = ref.competitors || {};
  const repC = rep.competitors || {};
  const compKeys = new Set([...Object.keys(refC), ...Object.keys(repC)]);
  for (const key of compKeys) {
    const refV = refC[key];
    const repV = repC[key];
    if (refV == null && repV == null) continue;
    const delta2 = numDelta(refV, repV);
    rows.push({
      label: `Competitors (${key})`,
      referenceDisplay: refV != null ? `${refV} match${refV === 1 ? "" : "es"}` : null,
      replayDisplay: repV != null ? `${repV} match${repV === 1 ? "" : "es"}` : null,
      changed: refV !== repV,
      deltaIcon: repV === 0 && refV > 0 ? "🚫" : (repV > (refV || 0) ? "📈" : "📉"),
      deltaText: delta2 ? `Δ ${delta2}` : undefined,
    });
  }
  const refCs = ref.computed_style;
  const repCs = rep.computed_style;
  if (refCs || repCs) {
    const cssKeys = ["display", "visibility", "opacity", "pointerEvents", "position"];
    for (const key of cssKeys) {
      const a = refCs?.[key];
      const b = repCs?.[key];
      if (a == null && b == null) continue;
      if (a === b) continue;
      rows.push({
        label: `Computed ${key}`,
        referenceDisplay: a ?? null,
        replayDisplay: b ?? null,
        changed: true,
      });
    }
  }
  return rows;
}

function SnapshotDump({ label, snap }) {
  if (!snap) return null;
  return (
    <details className="mt-2">
      <summary className="text-[11px] font-semibold text-[#0F172A] cursor-pointer hover:text-[#3B82F6]">
        {label} snapshot (raw JSON)
      </summary>
      <pre className="mt-2 font-mono text-[10px] whitespace-pre-wrap break-words bg-[#F8FAFC] border border-[#E5E7EB] rounded-md p-3 max-h-72 overflow-y-auto">
        {JSON.stringify(snap, null, 2)}
      </pre>
    </details>
  );
}

/** Build the markdown blob copied to the clipboard. Designed to be
 *  pasted directly into the Emergent chat for triage — has just enough
 *  headers so the Emergent agent can quickly find the key facts. */
function buildMarkdownDiagnostics(step, run, diag) {
  const lines = [];
  lines.push(`# Probe step failure diagnostics`);
  lines.push("");
  lines.push(`- **Run id**: ${run?.id || diag?.run_context?.run_id || "?"}`);
  lines.push(`- **Test**: ${run?.test_name || diag?.run_context?.test_name || "?"} (${run?.test_ref || diag?.run_context?.test_ref || "?"})`);
  lines.push(`- **Step**: ${step.index + 1} — \`${step.action}\` — ${step.description}`);
  lines.push(`- **Environment**: ${diag?.run_context?.environment_name || "?"} (${diag?.run_context?.environment_type || "?"})`);
  lines.push(`- **Framework**: ${diag?.framework?.name || run?.detected_framework?.name || "?"}${diag?.framework?.version ? " " + diag.framework.version : ""}`);
  lines.push(`- **Browser engine**: ${diag?.browser?.engine || "?"}${diag?.browser?.browserbase_session_id ? " (BB session " + diag.browser.browserbase_session_id + ")" : ""}`);
  lines.push(`- **Probe build**: ${diag?.probe?.git_commit || "?"} (Playwright ${diag?.probe?.playwright || "?"}, Python ${diag?.probe?.python || "?"})`);
  lines.push("");
  lines.push(`## Error`);
  lines.push("```");
  lines.push(diag?.error_message || step.error || "(no message)");
  lines.push("```");
  if (diag?.ai_recovery) {
    const r = diag.ai_recovery;
    lines.push("");
    lines.push(`## 🤖 AI Recovery Engine (outcome: ${r.outcome})`);
    if (r.final_diagnosis) lines.push(`- **Diagnosis**: ${r.final_diagnosis} (${r.final_category || "unknown"})`);
    if (r.final_blocking_control) lines.push(`- **Blocking control**: ${r.final_blocking_control}`);
    if (r.missing_prerequisite) lines.push(`- **Missing prerequisite**: ${r.missing_prerequisite}`);
    if (r.recovery_goal) lines.push(`- **Recovery goal**: ${r.recovery_goal}`);
    if (r.recovery_action_summary) lines.push(`- **Recovery actions**: ${r.recovery_action_summary}`);
    if (r.used_hint) lines.push(`- **Learned hint**: replayed deterministically (fingerprint ${r.hint_fingerprint || "?"})`);
    if (r.step_retry) lines.push(`- **Step retry**: ${r.step_retry}`);
    (r.iterations || []).forEach((it) => {
      lines.push(`- ${it.source === "learned-hint" ? "Learned hint" : `Iteration ${it.n}`}: ${it.result || "?"}${it.diagnosis ? ` — ${it.diagnosis}` : ""}`);
      if (it.recovery_goal) lines.push(`  - Goal: ${it.recovery_goal}`);
      (it.plan || []).forEach((p, pi) => {
        lines.push(`  - plan ${pi + 1}: ${p.status === "passed" ? "✓" : p.status === "skipped" ? "–" : "✗"} ${summarizeAction(p.action)}${p.execution_error ? ` (${p.execution_error})` : ""}`);
      });
      (it.verification || []).forEach((v) => {
        lines.push(`  - ${v.passed ? "✓" : "✗"} ${v.kind}${v.detail ? ` (${v.detail})` : ""}`);
      });
      (it.goal_verification || []).forEach((v) => {
        lines.push(`  - goal ${v.passed ? "✓" : "✗"} ${v.kind}${v.detail ? ` (${v.detail})` : ""}`);
      });
      if (it.goal_check) {
        lines.push(
          `  - ${it.goal_check.passed ? "✓" : "→"} goal check: ${
            it.goal_check.passed
              ? `original target located (${it.goal_check.matched || "resolved"})`
              : "original target still absent"
          }`
        );
      }
    });
  }
  const ir = step.interstitial_recovery;
  if (ir?.engaged) {
    lines.push("");
    lines.push(`## 🛡️ Surprise Handling (outcome: ${ir.outcome})`);
    (ir.blockers_detected || []).forEach((b) => {
      lines.push(
        `- **Blocker**: ${b.category_guess || "unknown"}${b.text ? ` — “${String(b.text).slice(0, 160)}”` : ""}${
          b.buttons?.length ? ` [buttons: ${b.buttons.join(" | ")}]` : ""
        }`
      );
    });
    (ir.dismissed || []).forEach((d) => {
      lines.push(
        `- ✓ Dismissed ${d.kind || "blocker"} via ${d.method}${d.label ? ` ("${d.label}")` : ""} — ${d.source} classification${
          d.business_action ? " ⚠ BUSINESS ACTION" : ""
        }`
      );
    });
    (ir.skipped_unsafe || []).forEach((u) => {
      lines.push(`- ⚠ Left alone: ${u.kind || "blocker"} — ${u.reason}`);
    });
    (ir.dismiss_failed || []).forEach((f) => {
      lines.push(`- ✗ Could not dismiss: ${f.kind || "blocker"}`);
    });
    const vrr = ir.validation_recovery;
    if (vrr) {
      lines.push(
        `- 🧩 **Validation-guided field recovery** (outcome: ${vrr.outcome}${
          vrr.extraction_source ? `, extraction: ${vrr.extraction_source}` : ""
        })`
      );
      (vrr.messages || []).slice(0, 2).forEach((m) => lines.push(`  - message: “${m}”`));
      if (vrr.fields_extracted?.length) lines.push(`  - fields extracted: ${vrr.fields_extracted.join(", ")}`);
      if (vrr.controls_discovered?.length) lines.push(`  - controls discovered (${vrr.controls_discovered.length}): ${vrr.controls_discovered.join(" | ")}`);
      (vrr.field_plans || []).forEach((p) => {
        lines.push(`  - plan: ${p.field} → ${p.strategy}${p.detail ? ` (${p.detail})` : ""}${p.reason ? ` (${p.reason})` : ""}`);
      });
      (vrr.fields_recovered || []).forEach((f) => lines.push(`  - ✓ recovered: ${f.field}${f.detail ? ` — ${f.detail}` : ""}`));
      (vrr.fields_failed || []).forEach((f) => lines.push(`  - ✗ failed: ${f.field} — ${f.reason}`));
      if (vrr.acknowledge) lines.push(`  - acknowledge: ${vrr.acknowledge.ok ? vrr.acknowledge.method : "failed"}`);
      if (vrr.retry_action) {
        lines.push(
          `  - retry: ${vrr.retry_action.kind}${vrr.retry_action.description ? ` — ${vrr.retry_action.description}` : ""}${
            vrr.retry_action.blocked ? ` ⛔ ${vrr.retry_action.blocked}` : ""
          }`
        );
      }
    }
    if (ir.budget) {
      lines.push(
        `- budget: dismissals ${ir.budget.run_used}/${ir.budget.run_max}, LLM ${ir.budget.llm_used}/${ir.budget.llm_max}, validation ${
          ir.budget.validation_used ?? 0
        }/${ir.budget.validation_max ?? 0}`
      );
    }
  }
  if (diag?.resolution_attempts?.length) {
    lines.push("");
    lines.push(`## Selector strategies attempted (${diag.resolution_attempts.length})`);
    lines.push("| # | Strategy | Selector / Label | Duration | Outcome | Error |");
    lines.push("|---|---|---|---|---|---|");
    diag.resolution_attempts.forEach((a, idx) => {
      const sel = (a.selector || "").replace(/\|/g, "\\|").slice(0, 200);
      const err = (a.error || "").replace(/\|/g, "\\|").replace(/\n/g, " ").slice(0, 200);
      lines.push(`| ${idx + 1} | \`${a.strategy}\` | \`${sel}\` | ${a.duration_ms ?? 0}ms | ${a.ok ? "passed" : "miss"} | ${err} |`);
    });
  }
  if (diag?.page) {
    lines.push("");
    lines.push(`## Page context at failure`);
    lines.push("```");
    lines.push(`url:           ${diag.page.url || ""}`);
    lines.push(`title:         ${diag.page.title || ""}`);
    lines.push(`ready_state:   ${diag.page.ready_state || ""}`);
    lines.push(`frame_count:   ${diag.page.frame_count ?? ""}`);
    lines.push(`dom_node_count:${diag.page.dom_node_count ?? ""}`);
    lines.push(`dom_size_chars:${diag.page.dom_size_chars ?? ""}`);
    if (diag.page.iframe_urls?.length) {
      lines.push(`iframes:`);
      diag.page.iframe_urls.forEach((u) => lines.push(`  - ${u}`));
    }
    lines.push("```");
  }
  if (diag?.replay_snapshot && diag?.reference_snapshot?.snapshot) {
    lines.push("");
    lines.push(`## 📼 Recording vs failure diff`);
    lines.push(`Reference: ${diag.reference_snapshot.source === "record" ? "recording-time snapshot" : "last passing run"}` +
      (diag.reference_snapshot.captured_at ? ` (captured ${diag.reference_snapshot.captured_at})` : ""));
    const rows = buildDiffRows(diag.reference_snapshot.snapshot, diag.replay_snapshot);
    lines.push("| Field | Reference | Failure | Δ |");
    lines.push("|---|---|---|---|");
    rows.forEach((r) => {
      const ref = String(r.referenceDisplay && typeof r.referenceDisplay !== "object" ? r.referenceDisplay : (r.referenceDisplay ? "(complex value)" : "—")).replace(/\|/g, "\\|").slice(0, 200);
      const rep = String(r.replayDisplay && typeof r.replayDisplay !== "object" ? r.replayDisplay : (r.replayDisplay ? "(complex value)" : "—")).replace(/\|/g, "\\|").slice(0, 200);
      const delta = r.changed
        ? `${r.deltaIcon || "⚠"} ${r.deltaText || "changed"}`
        : "✓ same";
      lines.push(`| ${r.label} | ${ref} | ${rep} | ${delta} |`);
    });
  }
  if (diag?.step_config) {
    lines.push("");
    lines.push(`## Step config`);
    lines.push("```json");
    lines.push(JSON.stringify(diag.step_config, null, 2));
    lines.push("```");
  }
  if (diag?.console_messages?.length || diag?.page_errors?.length) {
    lines.push("");
    lines.push(`## Browser console / page errors`);
    lines.push("```");
    diag.console_messages?.forEach((m) => {
      lines.push(`[${m.type}] ${m.text}${m.location?.url ? ` (${m.location.url}:${m.location.lineNumber ?? "?"})` : ""}`);
    });
    diag.page_errors?.forEach((p) => lines.push(`[pageerror] ${p.message}`));
    lines.push("```");
  }
  if (diag?.network_failures?.length) {
    lines.push("");
    lines.push(`## Failed network requests`);
    lines.push("```");
    diag.network_failures.forEach((n) => {
      lines.push(`${n.method || "?"} ${n.url} — ${n.failure || ""}`);
    });
    lines.push("```");
  }
  if (diag?.python_traceback) {
    lines.push("");
    lines.push(`## Python traceback`);
    lines.push("```");
    lines.push(diag.python_traceback);
    lines.push("```");
  }
  return lines.join("\n");
}
